import type { ReactNode } from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface KPIProps {
  label: string;
  value: ReactNode;
  subValue?: string;
  delta?: { value: string; direction: 'up' | 'down' | 'flat' };
  icon?: ReactNode;
  accent?: 'brand' | 'success' | 'warn' | 'danger' | 'neutral';
}

const accentClasses: Record<NonNullable<KPIProps['accent']>, string> = {
  brand:   'before:bg-brand-500',
  success: 'before:bg-success',
  warn:    'before:bg-warn',
  danger:  'before:bg-danger',
  neutral: 'before:bg-ink-soft',
};

export function KPI({ label, value, subValue, delta, icon, accent = 'brand' }: KPIProps) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg border border-border bg-surface p-5 shadow-card transition-[box-shadow,transform] hover:shadow-lift hover:-translate-y-[1px]',
        "before:absolute before:inset-x-0 before:top-0 before:h-[3px]",
        accentClasses[accent]
      )}
    >
      <div className="flex items-start justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">{label}</span>
        {icon && <span className="text-ink-soft">{icon}</span>}
      </div>
      <div className="mt-3 flex items-end gap-2">
        <span className="num text-[28px] font-semibold leading-none text-ink">{value}</span>
        {subValue && <span className="num text-[13px] text-ink-mute">{subValue}</span>}
      </div>
      {delta && (
        <div
          className={cn(
            'mt-3 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
            delta.direction === 'up' && 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
            delta.direction === 'down' && 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
            delta.direction === 'flat' && 'bg-stone-100 text-ink-mute dark:bg-white/5'
          )}
        >
          {delta.direction === 'up' && <ArrowUpRight size={12} />}
          {delta.direction === 'down' && <ArrowDownRight size={12} />}
          <span className="num">{delta.value}</span>
        </div>
      )}
    </div>
  );
}
